import { useCallback, useEffect, useState } from 'react';
import { CLASES_DOCUMENTO } from '@scm/contratos';
import { api } from '../lib/api.js';
import { entero, fechaHora } from '../lib/formato.js';
import { opciones, type Opcion } from '../lib/opciones.js';
import { txt, TXT_DOCUMENTO } from '../lib/enums.js';
import { Pagehead } from '../piezas/Cascaron.jsx';
import { Cargando, Error as AvisoError, Toast, Vacio } from '../piezas/Estados.jsx';
import { useSesion } from '../piezas/Sesion.jsx';

/**
 * Biblioteca de documentos del proyecto.
 *
 * El manual de la imagen de sistema fue quien la necesito —la especificacion
 * del second touch la daba por existente y no existia—, pero la biblioteca es
 * del PROYECTO: un plano, un procedimiento o un acta viven aqui igual.
 */

interface Documento {
  id: string;
  proyecto_id: string;
  proyecto: string;
  proyecto_nombre: string;
  cliente: string;
  clase: string;
  titulo: string;
  nombre: string;
  mime: string;
  bytes: number;
  nota: string | null;
  cargado_por_nombre: string | null;
  creado_en: string;
}

/** Un tamano en bytes, legible. */
function pesa(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function Biblioteca(): JSX.Element {
  const [proyectos, setProyectos] = useState<Opcion[]>([]);
  const [proyecto, setProyecto] = useState('');
  const [filas, setFilas] = useState<Documento[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [subiendo, setSubiendo] = useState(false);
  const { puede } = useSesion();

  useEffect(() => {
    opciones('proyectos').then(setProyectos).catch(setError);
  }, []);

  const cargar = useCallback(() => {
    setFilas(null);
    api
      .obtener<Documento[]>(`/v1/documentos${proyecto === '' ? '' : `?proyecto_id=${proyecto}`}`)
      .then(({ datos }) => setFilas(datos))
      .catch(setError);
  }, [proyecto]);

  useEffect(cargar, [cargar]);

  async function subir(archivo: File, clase: string, titulo: string): Promise<void> {
    setSubiendo(true);
    setError(null);
    try {
      const bytes = new Uint8Array(await archivo.arrayBuffer());
      let binario = '';
      for (const b of bytes) binario += String.fromCharCode(b);
      await api.enviar('/v1/documentos', {
        proyecto_id: proyecto,
        clase,
        titulo,
        nombre: archivo.name,
        mime: archivo.type,
        contenido_base64: btoa(binario),
      });
      setAviso(`${titulo} cargado.`);
      cargar();
    } catch (e) {
      setError(e);
    } finally {
      setSubiendo(false);
    }
  }

  async function darDeBaja(d: Documento): Promise<void> {
    setError(null);
    try {
      await api.quitar(`/v1/documentos/${d.id}`);
      setAviso(`${d.titulo} dado de baja.`);
      cargar();
    } catch (e) {
      setError(e);
    }
  }

  return (
    <>
      <Pagehead />

      <div className="fila wrap" style={{ marginBottom: 12, gap: 12 }}>
        <label className="campo" style={{ maxWidth: 420 }}>
          <span>Proyecto</span>
          <select
            value={proyecto}
            onChange={(ev) => setProyecto(ev.target.value)}
            data-prueba="proyecto-biblioteca"
          >
            <option value="">Todos los proyectos</option>
            {proyectos.map((p) => (
              <option key={p.id} value={p.id}>
                {p.etiqueta}
              </option>
            ))}
          </select>
        </label>

        <span className="crece" />

        {puede('MOD_PROYECTOS', 'escribir') && proyecto !== '' && (
          <SubirDocumento subiendo={subiendo} alSubir={(a, c, t) => void subir(a, c, t)} />
        )}
      </div>

      {error !== null && <AvisoError error={error} />}

      <div className="card p0">
        {filas === null ? (
          <Cargando que="los documentos" />
        ) : filas.length === 0 ? (
          <Vacio
            mensaje={
              proyecto === ''
                ? 'Todavia no hay documentos'
                : 'Este proyecto no tiene documentos cargados'
            }
            ayuda="Aqui vive el manual de la imagen de sistema, los planos y los procedimientos."
            icono="📚"
          />
        ) : (
          <table data-prueba="tabla-documentos">
            <thead>
              <tr>
                <th>Titulo</th>
                <th>Clase</th>
                <th>Proyecto</th>
                <th>Archivo</th>
                <th className="sec">Cargado</th>
                {puede('MOD_PROYECTOS', 'escribir') && <th />}
              </tr>
            </thead>
            <tbody>
              {filas.map((d) => (
                <tr key={d.id}>
                  <td>
                    {d.titulo}
                    {d.nota !== null && (
                      <div className="muted" style={{ fontSize: 11 }}>
                        {d.nota}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className="tag t-cold">{txt(TXT_DOCUMENTO, d.clase)}</span>
                  </td>
                  <td>
                    <span className="folio">{d.proyecto}</span>
                    <div className="muted" style={{ fontSize: 11 }}>
                      {d.cliente}
                    </div>
                  </td>
                  <td>
                    {d.nombre}
                    <div className="muted" style={{ fontSize: 11 }}>
                      {pesa(d.bytes)} · {d.mime}
                    </div>
                  </td>
                  <td className="sec muted" style={{ fontSize: 12 }}>
                    {fechaHora(d.creado_en)}
                    {d.cargado_por_nombre !== null && (
                      <div style={{ fontSize: 11 }}>{d.cargado_por_nombre}</div>
                    )}
                  </td>
                  {puede('MOD_PROYECTOS', 'escribir') && (
                    <td>
                      <button
                        type="button"
                        className="btn sm ghost"
                        onClick={() => void darDeBaja(d)}
                        data-prueba={`baja-${d.id}`}
                      >
                        Dar de baja
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {filas !== null && filas.length > 0 && (
        <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>
          {entero(filas.length)} {filas.length === 1 ? 'documento' : 'documentos'}. El archivo no se
          borra: la ficha se da de baja y queda en la bitacora.
        </div>
      )}

      {aviso !== null && <Toast mensaje={aviso} />}
    </>
  );
}

/**
 * La carga.
 *
 * El titulo se propone con el nombre del archivo sin extension, porque es lo
 * que la persona acaba de elegir y teclearlo otra vez es trabajo de mas; pero
 * se puede cambiar, porque el nombre del archivo rara vez es un titulo.
 */
function SubirDocumento({
  subiendo,
  alSubir,
}: {
  subiendo: boolean;
  alSubir: (archivo: File, clase: string, titulo: string) => void;
}): JSX.Element {
  const [archivo, setArchivo] = useState<File | null>(null);
  const [clase, setClase] = useState<string>('DOC_MANUAL');
  const [titulo, setTitulo] = useState('');

  if (archivo === null) {
    return (
      <label className="btn pri sm" data-prueba="elegir-documento">
        + Cargar documento
        <input
          type="file"
          accept="application/pdf,image/*"
          style={{ display: 'none' }}
          onChange={(ev) => {
            const f = ev.target.files?.[0];
            if (f) {
              setArchivo(f);
              setTitulo(f.name.replace(/\.[^.]+$/, ''));
            }
          }}
        />
      </label>
    );
  }

  return (
    <div className="card fila wrap" style={{ gap: 8 }} data-prueba="carga-documento">
      <span className="folio">{archivo.name}</span>
      <label className="campo" style={{ minWidth: 200 }}>
        <span>Titulo</span>
        <input value={titulo} onChange={(ev) => setTitulo(ev.target.value)} />
      </label>
      <label className="campo" style={{ minWidth: 180 }}>
        <span>Clase</span>
        <select value={clase} onChange={(ev) => setClase(ev.target.value)}>
          {CLASES_DOCUMENTO.map((c) => (
            <option key={c} value={c}>
              {txt(TXT_DOCUMENTO, c)}
            </option>
          ))}
        </select>
      </label>
      <button
        type="button"
        className="btn ghost sm"
        onClick={() => {
          setArchivo(null);
          setTitulo('');
        }}
      >
        Cancelar
      </button>
      <button
        type="button"
        className="btn pri sm"
        disabled={titulo.trim().length < 2 || subiendo}
        onClick={() => {
          alSubir(archivo, clase, titulo.trim());
          setArchivo(null);
          setTitulo('');
        }}
        data-prueba="guardar-documento"
      >
        {subiendo ? 'Cargando…' : 'Cargar'}
      </button>
    </div>
  );
}
